/**
 * libsodium: Ed25519 auth, X25519 crypto_box — must match server auth-flow / messages.
 */
import _sodium from "libsodium-wrappers";

let sodium: typeof _sodium | null = null;

export async function initSodium(): Promise<typeof _sodium> {
  if (sodium) return sodium;
  await _sodium.ready;
  sodium = _sodium;
  return _sodium;
}

export function getSodium(): typeof _sodium {
  if (!sodium) throw new Error("libsodium not ready — call initSodium()");
  return sodium;
}

export function u8ToB64(u8: Uint8Array): string {
  let bin = "";
  u8.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin);
}

export function b64ToU8(s: string): Uint8Array {
  const bin = atob(s);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

export type IdentityKeys = {
  userId: string;
  username: string;
  publicKeyEd: Uint8Array;
  secretKeyEd: Uint8Array;
  publicKeyX: Uint8Array;
  secretKeyX: Uint8Array;
};

/** Generate new identity (registration). */
export async function generateIdentity(username: string): Promise<IdentityKeys> {
  const s = getSodium();
  const userId = crypto.randomUUID();
  const { publicKey, privateKey } = s.crypto_sign_keypair();
  const pkX = s.crypto_sign_ed25519_pk_to_curve25519(publicKey);
  const skX = s.crypto_sign_ed25519_sk_to_curve25519(privateKey);
  return {
    userId,
    username,
    publicKeyEd: publicKey,
    secretKeyEd: privateKey,
    publicKeyX: pkX,
    secretKeyX: skX,
  };
}

/** Restore from persisted base64 keys. */
export async function identityFromStored(
  userId: string,
  username: string,
  publicKeyEdB64: string,
  secretKeyEdB64: string,
): Promise<IdentityKeys> {
  const s = getSodium();
  const publicKeyEd = b64ToU8(publicKeyEdB64);
  const secretKeyEd = b64ToU8(secretKeyEdB64);
  const pkX = s.crypto_sign_ed25519_pk_to_curve25519(publicKeyEd);
  const skX = s.crypto_sign_ed25519_sk_to_curve25519(secretKeyEd);
  return {
    userId,
    username,
    publicKeyEd,
    secretKeyEd,
    publicKeyX: pkX,
    secretKeyX: skX,
  };
}

export function signChallenge(nonce: Uint8Array, secretKeyEd: Uint8Array): Uint8Array {
  const s = getSodium();
  return s.crypto_sign_detached(nonce, secretKeyEd);
}

export function encryptDirectMessage(
  plaintextUtf8: string,
  recipientPkX: Uint8Array,
  mySkX: Uint8Array,
): { nonce: Uint8Array; ciphertext: Uint8Array } {
  const s = getSodium();
  const msg = s.from_string(plaintextUtf8);
  const nonce = s.randombytes_buf(s.crypto_box_NONCEBYTES);
  const ciphertext = s.crypto_box_easy(msg, nonce, recipientPkX, mySkX);
  return { nonce, ciphertext };
}

export function decryptDirectMessage(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  senderPkX: Uint8Array,
  mySkX: Uint8Array,
): string {
  const s = getSodium();
  const opened = s.crypto_box_open_easy(ciphertext, nonce, senderPkX, mySkX);
  return s.to_string(opened);
}

const VAULT_SYMMETRIC_DOMAIN = "styx-vault-symmetric-v1";

/** 32-byte symmetric key for vault message secretbox (derived from X25519 secret key). */
export function deriveVaultKey(secretKeyX25519: Uint8Array): Uint8Array {
  const s = getSodium();
  const domain = s.from_string(VAULT_SYMMETRIC_DOMAIN);
  return s.crypto_generichash(32, domain, secretKeyX25519);
}

export function encryptVaultMessage(
  plaintextUtf8: string,
  vaultKey: Uint8Array,
): { nonce: Uint8Array; ciphertext: Uint8Array } {
  const s = getSodium();
  const msg = s.from_string(plaintextUtf8);
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const ciphertext = s.crypto_secretbox_easy(msg, nonce, vaultKey);
  return { nonce, ciphertext };
}

export function decryptVaultMessage(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  vaultKey: Uint8Array,
): string {
  const s = getSodium();
  const opened = s.crypto_secretbox_open_easy(ciphertext, nonce, vaultKey);
  return s.to_string(opened);
}

/** IndexedDB / UI peer key for a vault thread (not a real user id). */
export function vaultPeerKey(vaultId: string): string {
  return `vault:${vaultId}`;
}

/** Encrypted chat envelope (UTF-8 JSON string, then crypto_box). */
export type MessageEnvelope =
  | {
      content_type: "text";
      body: string;
      reply_to: string | null;
      attachments: unknown[];
    }
  | {
      content_type: "image";
      body: string;
      reply_to: string | null;
      attachments: ChatImageAttachmentMeta[];
    }
  | {
      content_type: "file";
      body: string;
      reply_to: string | null;
      attachments: ChatFileAttachmentMeta[];
    }
  | {
      content_type: "voice";
      body: string;
      reply_to: string | null;
      attachments: ChatVoiceAttachmentMeta[];
    }
  | {
      content_type: "reaction";
      body: "";
      reply_to: null;
      attachments: [];
      meta: ReactionMeta;
    }
  | {
      content_type: "deleted";
      body: "";
      reply_to: null;
      attachments: [];
      v?: number;
    };

/** DM + workspace UI: normalized plaintext for a deleted message (E2E envelope, never sent as DM ciphertext after delete). */
export const TOMBSTONE_ENVELOPE_PLAINTEXT = JSON.stringify({
  content_type: "deleted",
  v: 1,
  body: "",
  reply_to: null,
  attachments: [],
});

/** DM: metadata + secretbox key; blob on disk is ciphertext. */
export type ImageAttachmentMetaE2e = {
  kind: "image";
  blob_id: string;
  mime: string;
  width: number;
  height: number;
  cipher_byte_length: number;
  key_b64: string;
  nonce_b64: string;
};

/** Workspace topic: server-stored image; members fetch via JWT. */
export type WorkspaceServerImageAttachment = {
  kind: "image";
  storage: "server";
  workspace_id: string;
  blob_id: string;
  mime: string;
  width: number;
  height: number;
};

export type ChatImageAttachmentMeta = ImageAttachmentMetaE2e | WorkspaceServerImageAttachment;

/** @deprecated Use ChatImageAttachmentMeta or ImageAttachmentMetaE2e */
export type ImageAttachmentMeta = ChatImageAttachmentMeta;

/** DM: file ciphertext + secretbox key. */
export type FileAttachmentMetaE2e = {
  kind: "file";
  blob_id: string;
  mime: string;
  filename: string;
  cipher_byte_length: number;
  key_b64: string;
  nonce_b64: string;
};

/** Workspace topic: server-stored file blob. */
export type WorkspaceServerFileAttachment = {
  kind: "file";
  storage: "server";
  workspace_id: string;
  blob_id: string;
  mime: string;
  filename: string;
  byte_length: number;
};

export type ChatFileAttachmentMeta = FileAttachmentMetaE2e | WorkspaceServerFileAttachment;

export type ChatMediaAttachmentMeta = ChatImageAttachmentMeta | ChatFileAttachmentMeta;

/** DM: encrypted voice blob + secretbox key. waveform: ~64 values 0–1. */
export type VoiceAttachmentMetaE2e = {
  kind: "voice";
  blob_id: string;
  mime: string;
  duration_ms: number;
  waveform: number[];
  cipher_byte_length: number;
  key_b64: string;
  nonce_b64: string;
};

/** Workspace topic: server-stored voice blob. */
export type WorkspaceServerVoiceAttachment = {
  kind: "voice";
  storage: "server";
  workspace_id: string;
  blob_id: string;
  mime: string;
  duration_ms: number;
  waveform: number[];
};

export type ChatVoiceAttachmentMeta = VoiceAttachmentMetaE2e | WorkspaceServerVoiceAttachment;

export function isWorkspaceServerVoiceAttachment(x: unknown): x is WorkspaceServerVoiceAttachment {
  if (typeof x !== "object" || x === null) return false;
  const o = x as WorkspaceServerVoiceAttachment;
  return (
    o.kind === "voice" &&
    o.storage === "server" &&
    typeof o.workspace_id === "string" &&
    typeof o.blob_id === "string" &&
    typeof o.mime === "string" &&
    typeof o.duration_ms === "number"
  );
}

export function isE2eVoiceAttachment(x: ChatVoiceAttachmentMeta): x is VoiceAttachmentMetaE2e {
  if (isWorkspaceServerVoiceAttachment(x)) return false;
  const e = x as VoiceAttachmentMetaE2e;
  return (
    typeof e.key_b64 === "string" &&
    typeof e.nonce_b64 === "string" &&
    typeof e.cipher_byte_length === "number"
  );
}

export function isChatVoiceAttachment(x: unknown): x is ChatVoiceAttachmentMeta {
  if (typeof x !== "object" || x === null) return false;
  if ((x as { kind?: string }).kind !== "voice" || typeof (x as { blob_id?: string }).blob_id !== "string") {
    return false;
  }
  if (isWorkspaceServerVoiceAttachment(x)) return true;
  return isE2eVoiceAttachment(x as ChatVoiceAttachmentMeta);
}

export function isWorkspaceServerImageAttachment(x: unknown): x is WorkspaceServerImageAttachment {
  if (typeof x !== "object" || x === null) return false;
  const o = x as WorkspaceServerImageAttachment;
  return (
    o.kind === "image" &&
    o.storage === "server" &&
    typeof o.workspace_id === "string" &&
    typeof o.blob_id === "string" &&
    typeof o.mime === "string" &&
    typeof o.width === "number" &&
    typeof o.height === "number"
  );
}

export function isE2eImageAttachment(x: ChatImageAttachmentMeta): x is ImageAttachmentMetaE2e {
  if (isWorkspaceServerImageAttachment(x)) return false;
  const e = x as ImageAttachmentMetaE2e;
  return (
    typeof e.key_b64 === "string" &&
    typeof e.nonce_b64 === "string" &&
    typeof e.cipher_byte_length === "number"
  );
}

export function isChatImageAttachment(x: unknown): x is ChatImageAttachmentMeta {
  if (typeof x !== "object" || x === null) return false;
  if ((x as { kind?: string }).kind !== "image" || typeof (x as { blob_id?: string }).blob_id !== "string") {
    return false;
  }
  if (isWorkspaceServerImageAttachment(x)) return true;
  return isE2eImageAttachment(x as ChatImageAttachmentMeta);
}

export function isWorkspaceServerFileAttachment(x: unknown): x is WorkspaceServerFileAttachment {
  if (typeof x !== "object" || x === null) return false;
  const o = x as WorkspaceServerFileAttachment;
  return (
    o.kind === "file" &&
    o.storage === "server" &&
    typeof o.workspace_id === "string" &&
    typeof o.blob_id === "string" &&
    typeof o.mime === "string" &&
    typeof o.filename === "string" &&
    typeof o.byte_length === "number"
  );
}

export function isE2eFileAttachment(x: ChatFileAttachmentMeta): x is FileAttachmentMetaE2e {
  if (isWorkspaceServerFileAttachment(x)) return false;
  const e = x as FileAttachmentMetaE2e;
  return (
    typeof e.key_b64 === "string" &&
    typeof e.nonce_b64 === "string" &&
    typeof e.cipher_byte_length === "number" &&
    typeof e.filename === "string"
  );
}

export function isChatFileAttachment(x: unknown): x is ChatFileAttachmentMeta {
  if (typeof x !== "object" || x === null) return false;
  if ((x as { kind?: string }).kind !== "file" || typeof (x as { blob_id?: string }).blob_id !== "string") {
    return false;
  }
  if (isWorkspaceServerFileAttachment(x)) return true;
  return isE2eFileAttachment(x as ChatFileAttachmentMeta);
}

export type ReactionAction = "add" | "remove";

export type ReactionMeta = {
  target_msg_id: string;
  emoji: string;
  action: ReactionAction;
};

export function envelopeText(body: string, replyTo: string | null = null): string {
  return JSON.stringify({
    content_type: "text",
    body,
    reply_to: replyTo,
    attachments: [],
  } satisfies MessageEnvelope);
}

export function envelopeImage(
  caption: string,
  attachment: ImageAttachmentMetaE2e,
  replyTo: string | null = null,
): string {
  return JSON.stringify({
    content_type: "image",
    body: caption,
    reply_to: replyTo,
    attachments: [attachment],
  } satisfies MessageEnvelope);
}

export function envelopeFile(
  caption: string,
  attachment: FileAttachmentMetaE2e,
  replyTo: string | null = null,
): string {
  return JSON.stringify({
    content_type: "file",
    body: caption,
    reply_to: replyTo,
    attachments: [attachment],
  } satisfies MessageEnvelope);
}

/** Stored in `workspace_messages.body`; `reply_to` lives in the DB column, not inside this JSON. */
export function workspaceTopicImageBodyJson(
  caption: string,
  attachment: WorkspaceServerImageAttachment,
): string {
  return JSON.stringify({
    content_type: "image",
    body: caption,
    attachments: [attachment],
  });
}

/** Stored in `workspace_messages.body`; `reply_to` is only in the DB column. */
export function workspaceTopicFileBodyJson(
  caption: string,
  attachment: WorkspaceServerFileAttachment,
): string {
  return JSON.stringify({
    content_type: "file",
    body: caption,
    attachments: [attachment],
  });
}

export function workspaceTopicVoiceBodyJson(attachment: WorkspaceServerVoiceAttachment): string {
  return JSON.stringify({
    content_type: "voice",
    body: "",
    attachments: [attachment],
  });
}

type WorkspaceTopicImageEnvelopeParsed = {
  caption: string;
  attachment: WorkspaceServerImageAttachment;
};

function parseWorkspaceTopicImageEnvelopeObject(raw: unknown): WorkspaceTopicImageEnvelopeParsed | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as {
    content_type?: unknown;
    body?: unknown;
    attachments?: unknown;
  };
  if (v.content_type !== "image") return null;
  if (!Array.isArray(v.attachments) || v.attachments.length < 1) return null;
  const first = v.attachments[0];
  if (!isWorkspaceServerImageAttachment(first)) return null;
  return {
    caption: typeof v.body === "string" ? v.body : "",
    attachment: first,
  };
}

/**
 * Workspace topics can include legacy payloads where an image envelope was wrapped into text body.
 * This parser accepts both direct image JSON and a text envelope whose body is image JSON.
 */
export function parseWorkspaceTopicImageEnvelope(plaintext: string): WorkspaceTopicImageEnvelopeParsed | null {
  try {
    const parsed = JSON.parse(plaintext) as unknown;
    const direct = parseWorkspaceTopicImageEnvelopeObject(parsed);
    if (direct) return direct;

    if (!parsed || typeof parsed !== "object") return null;
    const v = parsed as { content_type?: unknown; body?: unknown };
    if (v.content_type !== "text" || typeof v.body !== "string") return null;

    const nested = JSON.parse(v.body) as unknown;
    return parseWorkspaceTopicImageEnvelopeObject(nested);
  } catch {
    return null;
  }
}

export function workspaceTopicImageEnvelope(
  caption: string,
  attachment: WorkspaceServerImageAttachment,
  replyTo: string | null,
): string {
  return JSON.stringify({
    content_type: "image",
    body: caption,
    reply_to: replyTo,
    attachments: [attachment],
  } satisfies MessageEnvelope);
}

type WorkspaceTopicFileEnvelopeParsed = {
  caption: string;
  attachment: WorkspaceServerFileAttachment;
};

function parseWorkspaceTopicFileEnvelopeObject(raw: unknown): WorkspaceTopicFileEnvelopeParsed | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as {
    content_type?: unknown;
    body?: unknown;
    attachments?: unknown;
  };
  if (v.content_type !== "file") return null;
  if (!Array.isArray(v.attachments) || v.attachments.length < 1) return null;
  const first = v.attachments[0];
  if (!isWorkspaceServerFileAttachment(first)) return null;
  return {
    caption: typeof v.body === "string" ? v.body : "",
    attachment: first,
  };
}

export function parseWorkspaceTopicFileEnvelope(plaintext: string): WorkspaceTopicFileEnvelopeParsed | null {
  try {
    const parsed = JSON.parse(plaintext) as unknown;
    const direct = parseWorkspaceTopicFileEnvelopeObject(parsed);
    if (direct) return direct;

    if (!parsed || typeof parsed !== "object") return null;
    const v = parsed as { content_type?: unknown; body?: unknown };
    if (v.content_type !== "text" || typeof v.body !== "string") return null;

    const nested = JSON.parse(v.body) as unknown;
    return parseWorkspaceTopicFileEnvelopeObject(nested);
  } catch {
    return null;
  }
}

export function workspaceTopicFileEnvelope(
  caption: string,
  attachment: WorkspaceServerFileAttachment,
  replyTo: string | null,
): string {
  return JSON.stringify({
    content_type: "file",
    body: caption,
    reply_to: replyTo,
    attachments: [attachment],
  } satisfies MessageEnvelope);
}

export function envelopeVoice(
  attachment: VoiceAttachmentMetaE2e,
  replyTo: string | null = null,
): string {
  return JSON.stringify({
    content_type: "voice",
    body: "",
    reply_to: replyTo,
    attachments: [attachment],
  } satisfies MessageEnvelope);
}

export function workspaceTopicVoiceEnvelope(
  attachment: WorkspaceServerVoiceAttachment,
  replyTo: string | null,
): string {
  return JSON.stringify({
    content_type: "voice",
    body: "",
    reply_to: replyTo,
    attachments: [attachment],
  } satisfies MessageEnvelope);
}

export function envelopeReaction(
  targetMessageId: string,
  emoji: string,
  action: ReactionAction,
): string {
  return JSON.stringify({
    content_type: "reaction",
    body: "",
    reply_to: null,
    attachments: [],
    meta: {
      target_msg_id: targetMessageId,
      emoji,
      action,
    },
  } satisfies MessageEnvelope);
}

function normalizeReplyToField(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  return t.length > 0 ? t : null;
}

function normalizeReactionMeta(raw: unknown): ReactionMeta | null {
  if (!raw || typeof raw !== "object") return null;
  const maybe = raw as Partial<ReactionMeta>;
  const target = typeof maybe.target_msg_id === "string" ? maybe.target_msg_id.trim() : "";
  const emoji = typeof maybe.emoji === "string" ? maybe.emoji.trim() : "";
  const action = maybe.action === "remove" ? "remove" : maybe.action === "add" ? "add" : null;
  if (!target || !emoji || !action) return null;
  return {
    target_msg_id: target,
    emoji,
    action,
  };
}

export function parseMessageEnvelope(plaintext: string): MessageEnvelope | null {
  try {
    const v = JSON.parse(plaintext) as MessageEnvelope & { reply_to?: unknown; meta?: unknown };
    if (!v || typeof v !== "object") return null;
    const reply_to = normalizeReplyToField(v.reply_to);
    if (v.content_type === "text") {
      return { ...v, reply_to, attachments: Array.isArray(v.attachments) ? v.attachments : [] };
    }
    if (v.content_type === "image") {
      return { ...v, reply_to, attachments: Array.isArray(v.attachments) ? v.attachments : [] };
    }
    if (v.content_type === "file") {
      return { ...v, reply_to, attachments: Array.isArray(v.attachments) ? v.attachments : [] };
    }
    if (v.content_type === "voice") {
      return { ...v, reply_to, attachments: Array.isArray(v.attachments) ? v.attachments : [] };
    }
    if (v.content_type === "reaction") {
      const meta = normalizeReactionMeta(v.meta);
      if (!meta) return null;
      return {
        ...v,
        body: "",
        reply_to: null,
        attachments: [],
        meta,
      };
    }
    if (v.content_type === "deleted") {
      return {
        content_type: "deleted",
        body: "",
        reply_to: null,
        attachments: [],
        v: typeof v.v === "number" ? v.v : 1,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function isTombstonePlaintext(plaintext: string): boolean {
  return parseMessageEnvelope(plaintext)?.content_type === "deleted";
}

/** `reply_to` message id from envelope, if any. */
export function envelopeReplyToId(plaintext: string): string | null {
  const env = parseMessageEnvelope(plaintext);
  if (!env) return null;
  if (env.content_type === "reaction" || env.content_type === "deleted") return null;
  return env.reply_to;
}

/** Server-side blob GC: E2E image/file/voice envelopes reference one `blob_id` each. */
export function e2eAttachmentBlobIdsFromPlaintext(plaintext: string): string[] {
  const env = parseMessageEnvelope(plaintext);
  if (!env) return [];
  if (env.content_type !== "image" && env.content_type !== "file" && env.content_type !== "voice") {
    return [];
  }
  const a0 = env.attachments[0];
  if (!a0 || typeof a0 !== "object") return [];
  const bid = (a0 as { blob_id?: unknown }).blob_id;
  if (typeof bid !== "string" || bid.length < 32) return [];
  return [bid];
}

export function envelopeReactionMeta(plaintext: string): ReactionMeta | null {
  const env = parseMessageEnvelope(plaintext);
  return env?.content_type === "reaction" ? env.meta : null;
}

/** Text/caption for bubbles; supports legacy non-JSON plaintext. */
export function plaintextDisplayBody(plaintext: string): string {
  const env = parseMessageEnvelope(plaintext);
  if (env?.content_type === "text") return env.body;
  if (env?.content_type === "image") return env.body ?? "";
  if (env?.content_type === "file") return env.body ?? "";
  if (env?.content_type === "voice") return "";
  if (env?.content_type === "reaction") return "Реакция";
  if (env?.content_type === "deleted") return "Сообщение удалено";
  try {
    const parsed = JSON.parse(plaintext) as { body?: string };
    return typeof parsed.body === "string" ? parsed.body : plaintext;
  } catch {
    return plaintext;
  }
}

/** One-line preview for reply bar / quote (caption, «Фото» for image without caption). */
export function previewTextForReply(plaintext: string): string {
  const env = parseMessageEnvelope(plaintext);
  if (env?.content_type === "deleted") return "Сообщение удалено";
  if (env?.content_type === "image") {
    const cap = (env.body ?? "").trim();
    return cap || "Фото";
  }
  if (env?.content_type === "file") {
    const cap = (env.body ?? "").trim();
    if (cap) return cap;
    const att = env.attachments?.[0];
    if (isChatFileAttachment(att)) return att.filename || "Файл";
    return "Файл";
  }
  if (env?.content_type === "voice") return "🎤 Голосовое";
  if (env?.content_type === "reaction") return "Реакция";
  const line = plaintextDisplayBody(plaintext).trim();
  return line || "…";
}

/** Symmetric encryption for image bytes before upload (server stores ciphertext only). */
export function encryptImageBytes(plain: Uint8Array): {
  encrypted: Uint8Array;
  key: Uint8Array;
  nonce: Uint8Array;
} {
  const s = getSodium();
  const key = s.randombytes_buf(s.crypto_secretbox_KEYBYTES);
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const encrypted = s.crypto_secretbox_easy(plain, nonce, key);
  return { encrypted, key, nonce };
}

export function decryptImageBytes(encrypted: Uint8Array, nonce: Uint8Array, key: Uint8Array): Uint8Array {
  const s = getSodium();
  return s.crypto_secretbox_open_easy(encrypted, nonce, key);
}

/** Derive a 32-byte key for local encrypted caches (link preview, decrypted image bytes). */
export function deriveLocalCacheKey(secretKeyX: Uint8Array): Uint8Array {
  const s = getSodium();
  const ctx = new TextEncoder().encode("styx-at-rest-cache-v1");
  const msg = new Uint8Array(secretKeyX.length + ctx.length);
  msg.set(secretKeyX, 0);
  msg.set(ctx, secretKeyX.length);
  return s.crypto_generichash(32, msg);
}

/** Nonce + secretbox ciphertext (libsodium crypto_secretbox_easy). */
export function encryptAtRest(plain: Uint8Array, key: Uint8Array): Uint8Array {
  const s = getSodium();
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const box = s.crypto_secretbox_easy(plain, nonce, key);
  const out = new Uint8Array(nonce.length + box.length);
  out.set(nonce, 0);
  out.set(box, nonce.length);
  return out;
}

export function decryptAtRest(cipherWithNonce: Uint8Array, key: Uint8Array): Uint8Array {
  const s = getSodium();
  const n = s.crypto_secretbox_NONCEBYTES;
  const nonce = cipherWithNonce.subarray(0, n);
  const box = cipherWithNonce.subarray(n);
  return s.crypto_secretbox_open_easy(box, nonce, key);
}
